import { useState, useCallback, useMemo } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import { FetchApiIds } from '@/types';
import seoTasksService from '@/services/seoTasks';
import seoKeywordsService from '@/services/seoKeywords';
import seoPagesService from '@/services/seoPages';
import wordpressService from '@/services/wordpress';
import { SeoTask, SeoTrackedKeyword, SeoPage } from '@/types/api/seo';
import SeoTaskListComponent from './SeoTaskList.component';

type KeywordsPaginated = { data: SeoTrackedKeyword[]; count: number; currentPage: number; lastPage: number; nextPage: number | null; prevPage: number | null };

const SeoTaskListPage = function (): JSX.Element {
  const { project } = useSeoProject();
  const projectId = project?.id ?? '';
  const wpConnected = !!(project?.wpConnectedAt && project?.wpSiteUrl);

  const { data: tasks = [], isLoading: isLoadingTasks, refetch: refetchTasks } = useSeoFetch<SeoTask[]>(
    ['seo-project-tasks', projectId],
    () => seoTasksService.getByProject(projectId),
    { enabled: !!projectId }
  );

  const { data: keywordsData } = useSeoFetch<KeywordsPaginated>(
    [FetchApiIds.SEO_KEYWORDS, projectId],
    () => seoKeywordsService.getAll({ projectId, page: 1, take: 500 }),
    { enabled: !!projectId, retry: false }
  );

  const keywords: SeoTrackedKeyword[] = useMemo(
    () => keywordsData?.data ?? [],
    [keywordsData]
  );

  // Fetch pages to resolve targetPageId → URL
  const { data: pages = [] } = useSeoFetch<SeoPage[]>(
    ['seo-project-pages', projectId],
    () => seoPagesService.getByProject(projectId),
    { enabled: !!projectId, retry: false }
  );

  // Build a map: keywordId → page URL (target page preferred, ranking URL fallback)
  const keywordPageMap = useMemo(() => {
    const pageUrlMap = new Map<string, string>();
    for (const p of pages) pageUrlMap.set(p.id, p.url);

    const map = new Map<string, string>();
    for (const kw of keywords) {
      const targetUrl = kw.targetPageId ? pageUrlMap.get(kw.targetPageId) : null;
      const rankingUrl = kw.latestSnapshot?.url ?? null;
      const url = targetUrl ?? rankingUrl;
      if (url) map.set(kw.id, url);
    }
    return map;
  }, [keywords, pages]);

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    try {
      await seoTasksService.bulkDelete(ids);
      refetchTasks();
    } catch (err) {
      console.error('[SEOx] Bulk delete failed:', err);
    }
  }, [refetchTasks]);

  const handleStatusChange = useCallback(async (taskId: string, status: SeoTask['status']) => {
    try {
      if (status === 'dismissed') {
        await seoTasksService.delete(taskId);
      } else {
        await seoTasksService.updateStatus(taskId, status);
      }
      refetchTasks();
    } catch (err) {
      console.error('[SEOx] Task status update failed:', err);
    }
  }, [refetchTasks]);

  const handleApplyToWp = useCallback(async (task: SeoTask) => {
    if (!project || !task.suggestedValue) return;
    try {
      // Determine change type
      const changes: Record<string, string> = {};
      if (task.taskType === 'title_tag') {
        changes.seoTitle = task.suggestedValue;
      } else if (task.taskType === 'meta_description') {
        changes.seoDescription = task.suggestedValue;
      } else if (task.taskType === 'content' || task.taskType === 'schema') {
        // FAQ entries and schema markup get appended as content
        changes.content = task.suggestedValue;
      }

      // Find the WP post for the task's page
      // Try to find the post using the keyword's target page or first ranking page
      const keyword = task.keywordId ? keywords.find((k) => k.id === task.keywordId) : null;
      const pageUrl = keyword?.latestSnapshot?.url ?? '';

      if (pageUrl) {
        const wpPost = await wordpressService.findPostByUrl(project.id, pageUrl);
        if (wpPost) {
          const result = await wordpressService.applyChanges({
            projectId: project.id,
            postId: wpPost.postId,
            postType: wpPost.postType,
            keywordId: task.keywordId ?? undefined,
            pageUrl,
            changes,
          });

          if (result.success) {
            await seoTasksService.markAppliedToWp(task.id);
            refetchTasks();

            // Check verification
            const v = result.verification ?? {};
            const allVerified = Object.values(v).every((r) => r.verified);
            const failedFields = Object.entries(v).filter(([, r]) => !r.verified).map(([k]) => k);

            if (failedFields.length > 0) {
              console.warn(`[SEOx] WP verification failed for: ${failedFields.join(', ')}. The SEO plugin may need the bridge plugin installed.`);
            }
          }
        } else {
          console.error('[SEOx] Could not find WP post for URL:', pageUrl);
        }
      } else {
        console.error('[SEOx] No page URL found for task keyword');
      }
    } catch (err) {
      console.error('[SEOx] Apply to WP failed:', err);
    }
  }, [project, keywords, refetchTasks]);

  return (
    <SeoTaskListComponent
      tasks={tasks}
      keywords={keywords}
      keywordPageMap={keywordPageMap}
      isLoading={isLoadingTasks && !!projectId}
      onStatusChange={handleStatusChange}
      onBulkDelete={handleBulkDelete}
      onApplyToWp={handleApplyToWp}
      wpConnected={wpConnected}
    />
  );
};

export default SeoTaskListPage;
